import { Box, Flex, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { LOAN_PRODUCT_TYPE } from '~/common/constants';
import { formatCurrency } from '~/common/util/format';
import theme from '~/theme/';

import { useGetAssetsSummaryInformationQuery } from '../hooks';

const StyledBox = styled(Box)`
  width: 25%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 1rem;
`;

const StyledInfoText = styled(Text)`
  text-decoration: underline;
  text-decoration-color: ${theme?.colors?.gray[400]};
  font-size: 1.125rem;
  font-weight: 500;
`;

const StyledInfoValue = styled(Text)`
  color: ${theme?.colors?.gray[900]};
  font-size: 1.125rem;
  font-weight: 500;
`;

const TotalAssetInfoWindowContainer = styled(Flex)`
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  border: 0px solid ${theme?.colors?.blue[600]};
  border-top-width: 2px;
  border-bottom-width: 2px;
  padding-bottom: 1rem;
  width: 100%;
  margin-bottom: 1rem;
`;

type StyledInfoItemProps = {
  label: string;
  value: string;
};

const StyledInfoItem = ({ label, value }: StyledInfoItemProps) => (
  <StyledBox as="dl">
    <StyledInfoText as="dt">{label}</StyledInfoText>
    <StyledInfoValue as="dd">{value}</StyledInfoValue>
  </StyledBox>
);

export default function TotalAssetInfoWindow() {
  const { data, isLoading } = useGetAssetsSummaryInformationQuery();

  const { assetsSummaryInformation, loanProductType } = data ?? {};

  const {
    cashToFromBorrower,
    totalFundsAvailable,
    totalFundsAvailableFHA,
    largeDepositThreshold,
    totalReservesRequired,
    minimumInvestment,
    actualBorrowerInvestment,
    totalVerifiedFunds,
    subordinateFinancingAmount,
  } = assetsSummaryInformation ?? {};

  const isLoanFHA = loanProductType === LOAN_PRODUCT_TYPE.FHA;
  const isLoanVA = loanProductType === LOAN_PRODUCT_TYPE.VA;
  const assetScreenMriAlertEnabledEffectiveDate =
    data?.assetsFeatureFlags?.AssetScreenMriAlertEnabledEffectiveDate;

  if (isLoading) {
    return null;
  }

  return (
    <TotalAssetInfoWindowContainer>
      <StyledInfoItem
        label="Cash to/from Borrower"
        value={formatCurrency(cashToFromBorrower)}
      />
      <StyledInfoItem
        label="Max Funds Available"
        value={formatCurrency(
          isLoanFHA ? totalFundsAvailableFHA : totalFundsAvailable,
        )}
      />
      {!isLoanVA && (
        <StyledInfoItem
          label="Large Deposit Threshold"
          value={formatCurrency(largeDepositThreshold)}
        />
      )}

      <StyledInfoItem
        label="Total Required Reserves"
        value={formatCurrency(totalReservesRequired)}
      />
      {minimumInvestment > 0 && (
        <>
          <StyledInfoItem
            label={
              isLoanFHA && assetScreenMriAlertEnabledEffectiveDate
                ? 'Minimum Required Investment'
                : 'Required Minimum Investment'
            }
            value={formatCurrency(minimumInvestment)}
          />
          <StyledInfoItem
            label="Actual Borrower Investment"
            value={formatCurrency(actualBorrowerInvestment)}
          />
        </>
      )}
      {isLoanFHA && (
        <StyledInfoItem
          label="Total Verified Funds"
          value={formatCurrency(totalVerifiedFunds)}
        />
      )}
      {subordinateFinancingAmount !== 0 && (
        <StyledInfoItem
          label="New Subordinate Financing Amount"
          value={formatCurrency(subordinateFinancingAmount)}
        />
      )}
    </TotalAssetInfoWindowContainer>
  );
}
